import {
  Alert,
  Box,
  FormControl,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";
import carbonConfigService from "src/services/carbonConfigService";
import { Loader } from "../Loader";

// Self-contained editor for a team's carbon footprint config. It saves via the
// dedicated /carbon-config endpoint (not the team PUT), so it owns its own load,
// validation and Save/Reset buttons.
const TeamCarbonForm = ({ teamId, isReadOnly }) => {
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;

  const [config, setConfig] = useState(null); // raw per-team override (null when unset)
  const [gridEmissionFactor, setGridEmissionFactor] = useState("");
  const [renewablePercentage, setRenewablePercentage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const showAlert = (message, severity) =>
    dispatch(setAlert({ open: true, message, severity }));

  const hydrate = useCallback((cfg, res) => {
    const grid = cfg?.gridEmissionFactor ?? res?.GRID_EMISSION_FACTOR;
    const renewable =
      cfg?.renewablePercentage ??
      (res?.RENEWABLE_PERCENTAGE != null ? res.RENEWABLE_PERCENTAGE * 100 : "");
    setGridEmissionFactor(grid ?? "");
    setRenewablePercentage(renewable ?? "");
  }, []);

  const load = useCallback(async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      const response = await carbonConfigService.getTeamCarbonConfig(teamId);
      const cfg = response?.data?.config || null;
      const res = response?.data?.resolved || null;
      setConfig(cfg);
      hydrate(cfg, res);
    } catch (error) {
      showAlert("Failed to load carbon footprint settings.", enums.SEVERITY.ERROR);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, hydrate]);

  useEffect(() => {
    load();
  }, [load]);

  const validate = () => {
    const grid = Number(gridEmissionFactor);
    const renewable = Number(renewablePercentage);
    if (gridEmissionFactor === "" || Number.isNaN(grid) || grid < 0 || grid > 5) {
      showAlert(
        "Grid emission factor must be a number between 0 and 5 (kg CO₂/kWh).",
        enums.SEVERITY.ERROR
      );
      return false;
    }
    if (
      renewablePercentage === "" ||
      Number.isNaN(renewable) ||
      renewable < 0 ||
      renewable > 100
    ) {
      showAlert(
        "Renewable percentage must be a number between 0 and 100.",
        enums.SEVERITY.ERROR
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      await carbonConfigService.updateTeamCarbonConfig(teamId, {
        gridEmissionFactor: Number(gridEmissionFactor),
        renewablePercentage: Number(renewablePercentage),
      });
      showAlert("Carbon footprint settings saved.", enums.SEVERITY.SUCCESS);
      await load();
    } catch (error) {
      showAlert("Failed to save carbon footprint settings.", enums.SEVERITY.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    try {
      setSaving(true);
      // Sending null clears the override; the backend falls back to the
      // deployment default (env var, else built-in).
      await carbonConfigService.updateTeamCarbonConfig(teamId, {
        gridEmissionFactor: null,
        renewablePercentage: null,
      });
      showAlert("Reverted to the deployment default.", enums.SEVERITY.SUCCESS);
      await load();
    } catch (error) {
      showAlert("Failed to reset carbon footprint settings.", enums.SEVERITY.ERROR);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  const isOverridden = Boolean(
    config &&
      (config.gridEmissionFactor != null || config.renewablePercentage != null)
  );

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Carbon Footprint Settings
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        These factors drive the dashboard&apos;s CO₂, trees and avoided-emissions
        figures. Leave them at the default unless you have a region-specific value.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {isOverridden
          ? "This team uses custom values below. Reset to fall back to the deployment default."
          : "This team currently uses the deployment default. Saving will set a team-specific override."}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <FormControl fullWidth size="small">
            <TextField
              id="carbon-grid-emission-factor"
              label="Grid Emission Factor"
              size="small"
              variant="outlined"
              type="number"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">kg CO₂/kWh</InputAdornment>
                ),
              }}
              inputProps={{ min: 0, max: 5, step: "any" }}
              disabled={isReadOnly || saving}
              value={gridEmissionFactor}
              onChange={(e) => setGridEmissionFactor(e.target.value)}
              helperText="CO₂ emitted per kWh of grid electricity (e.g. 0.5 generic, ~0.71 India)."
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <FormControl fullWidth size="small">
            <TextField
              id="carbon-renewable-percentage"
              label="Renewable Share"
              size="small"
              variant="outlined"
              type="number"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100, step: "any" }}
              disabled={isReadOnly || saving}
              value={renewablePercentage}
              onChange={(e) => setRenewablePercentage(e.target.value)}
              helperText="Assumed share of energy from renewables (0-100)."
            />
          </FormControl>
        </Grid>
      </Grid>

      {!isReadOnly && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 4 }}
        >
          <LoadingButton
            size="large"
            variant="contained"
            onClick={handleSave}
            loading={saving}
          >
            Save
          </LoadingButton>
          <LoadingButton
            size="large"
            variant="outlined"
            color="secondary"
            onClick={handleResetToDefault}
            loading={saving}
            disabled={!isOverridden}
          >
            Reset to default
          </LoadingButton>
        </Stack>
      )}
    </Box>
  );
};

export default TeamCarbonForm;
